/**
 * Object Manager Page Layout Definitions
 *
 * Defines page layouts for detail pages across all objects.
 * Admins can control field grouping, section order, and layout configuration.
 */

import { createPersistedMockData } from '../mockDataManager';

/**
 * Layout Section Interface
 * Defines a group of fields on a detail page
 */
export interface LayoutSection {
  id: string;
  title: string;
  columns: 1 | 2; // Number of columns in this section
  fields: string[]; // Array of field API names
  order: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

/**
 * Page Layout Interface
 * Complete layout configuration for an object's detail page
 */
export interface PageLayout {
  id: string;
  name: string;
  objectApiName: string;
  sections: LayoutSection[];
  isDefault?: boolean;
  description?: string;
  applicationName?: string;
  createdBy?: string;
  createdDate?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
}

/**
 * DEFAULT PAGE LAYOUTS FOR ALL OBJECTS
 */
export const defaultPageLayouts: PageLayout[] = [
  // ============================================================
  // LEAD PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_lead_default',
    name: 'Lead Layout',
    objectApiName: 'Lead__c',
    isDefault: true,
    sections: [
      {
        id: 'section_lead_contact',
        title: 'Contact Information',
        columns: 2,
        fields: ['name', 'title', 'company', 'email', 'phone', 'owner'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_lead_details',
        title: 'Lead Details',
        columns: 2,
        fields: ['status', 'rating', 'leadSource', 'industry', 'numberOfEmployees', 'annualRevenue'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_lead_description',
        title: 'Additional Information',
        columns: 1,
        fields: ['description'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_lead_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdDate', 'lastContact'],
        order: 4,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // CLIENT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_client_default',
    name: 'Client Layout',
    objectApiName: 'Client__c',
    applicationName: 'Global',
    isDefault: true,
    sections: [
      {
        id: 'section_client_overview',
        title: 'Client Overview',
        columns: 2,
        fields: ['name', 'status', 'accountOwner', 'lastContact', 'industry', 'email'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_client_company',
        title: 'Company Information',
        columns: 2,
        fields: ['phone', 'website', 'annualRevenue', 'numberOfEmployees'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_client_address',
        title: 'Address Information',
        columns: 2,
        fields: ['billingAddress', 'shippingAddress'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_client_metrics',
        title: 'Client Metrics',
        columns: 2,
        fields: ['activeProjects', 'totalRevenue', 'contractEndDate'],
        order: 4,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_client_description',
        title: 'Description',
        columns: 1,
        fields: ['description'],
        order: 5,
        collapsible: true,
        defaultCollapsed: true
      },
      {
        id: 'section_client_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdDate', 'lastModified'],
        order: 6,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // CONTACT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_contact_default',
    name: 'Contact Layout',
    objectApiName: 'Contact__c',
    isDefault: true,
    sections: [
      {
        id: 'section_contact_info',
        title: 'Contact Information',
        columns: 2,
        fields: ['firstName', 'lastName', 'title', 'email', 'phone', 'mobilePhone', 'isPrimary'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_contact_company',
        title: 'Company Details',
        columns: 2,
        fields: ['accountId', 'department', 'owner'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_contact_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdDate', 'lastContact'],
        order: 3,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // OPPORTUNITY PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_opportunity_default',
    name: 'Opportunity Layout',
    objectApiName: 'Opportunity__c',
    isDefault: true,
    sections: [
      {
        id: 'section_opportunity_overview',
        title: 'Opportunity Information',
        columns: 2,
        fields: ['Name__c', 'ClientName__c', 'Amount__c', 'Stage__c', 'Probability__c', 'CloseDate__c'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_opportunity_details',
        title: 'Details',
        columns: 2,
        fields: ['Owner__c', 'Type__c', 'LeadSource__c', 'ContactName__c', 'NextStep__c'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_opportunity_financial',
        title: 'Financial Information',
        columns: 2,
        fields: ['Amount__c', 'ExpectedRevenue__c'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_opportunity_description',
        title: 'Description',
        columns: 1,
        fields: ['Description__c'],
        order: 4,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_opportunity_system',
        title: 'System Information',
        columns: 2,
        fields: ['CreatedDate__c', 'LastModifiedDate__c', 'CreatedBy__c'],
        order: 5,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // CASE PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_case_default',
    name: 'Case Layout',
    objectApiName: 'Case__c',
    isDefault: true,
    sections: [
      {
        id: 'section_case_overview',
        title: 'Case Information',
        columns: 2,
        fields: ['caseNumber', 'subject', 'subType', 'status', 'priority', 'type', 'origin'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_case_assignment',
        title: 'Assignment & Client',
        columns: 2,
        fields: ['owner', 'accountName', 'accountId'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_case_description',
        title: 'Description',
        columns: 1,
        fields: ['description'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_case_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdDate', 'lastModifiedDate'],
        order: 4,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // CREDIT BLOCK CASE PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_case_credit_block',
    name: 'Credit Block Case Layout',
    objectApiName: 'Case__c',
    isDefault: false,
    sections: [
      {
        id: 'section_cb_case_info',
        title: 'Case Information',
        columns: 2,
        fields: ['caseNumber', 'subject', 'subType', 'status', 'priority', 'type', 'origin'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_cb_case_assignment',
        title: 'Assignment & Client',
        columns: 2,
        fields: ['owner', 'accountName', 'accountId'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_cb_case_description',
        title: 'Description',
        columns: 1,
        fields: ['description'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_cb_case_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdDate', 'lastModifiedDate'],
        order: 4,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // EVENT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_event_default',
    name: 'Event Layout',
    objectApiName: 'Event__c',
    isDefault: true,
    sections: [
      {
        id: 'section_event_overview',
        title: 'Event Information',
        columns: 2,
        fields: ['subject', 'type', 'status', 'priority', 'startDateTime', 'endDateTime'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_event_details',
        title: 'Details',
        columns: 2,
        fields: ['owner', 'location', 'relatedToType', 'relatedTo'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_event_description',
        title: 'Description',
        columns: 1,
        fields: ['description'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_event_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdBy', 'createdDate', 'lastModifiedDate'],
        order: 4,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // TASK PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_task_default',
    name: 'Task Layout',
    objectApiName: 'Task__c',
    isDefault: true,
    sections: [
      {
        id: 'section_task_overview',
        title: 'Task Information',
        columns: 2,
        fields: ['title', 'status', 'priority', 'type', 'dueDate', 'assignee'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_task_details',
        title: 'Details',
        columns: 2,
        fields: ['source', 'clientName', 'assignedDate', 'completedDate', 'slaTarget'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_task_description',
        title: 'Description',
        columns: 1,
        fields: ['description'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_task_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdBy', 'createdDate', 'lastModifiedDate'],
        order: 4,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // CREDIT BLOCK TASK PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_task_credit_block',
    name: 'Credit Block Task Layout',
    objectApiName: 'Task__c',
    isDefault: false,
    sections: [
      {
        id: 'section_cb_task_info',
        title: 'Task Information',
        columns: 2,
        fields: ['title', 'status', 'priority', 'type', 'assignee', 'dueDate'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_cb_task_bureau',
        title: 'Bureau Request',
        columns: 2,
        fields: ['clientName', 'clientId', 'source', 'RelatedTo__c'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_cb_task_description',
        title: 'Description',
        columns: 1,
        fields: ['description'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_cb_task_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdBy', 'createdDate', 'lastModifiedDate', 'completedDate'],
        order: 4,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // REPORT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_report_default',
    name: 'Report Layout',
    objectApiName: 'Report__c',
    isDefault: true,
    sections: [
      {
        id: 'section_report_overview',
        title: 'Report Information',
        columns: 2,
        fields: ['name', 'objectName', 'type', 'format', 'status', 'createdBy'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_report_schedule',
        title: 'Schedule & Execution',
        columns: 2,
        fields: ['frequency', 'lastRunDate'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_report_description',
        title: 'Description',
        columns: 1,
        fields: ['description'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      }
    ]
  },

  // ============================================================
  // CYBERSWEEP CLIENT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_client_cybersweep',
    name: 'CyberSweep Client Layout',
    objectApiName: 'Client__c',
    applicationName: 'CyberSweep',
    isDefault: false,
    description: 'Specialized layout for CyberSweep client records',
    sections: [
      {
        id: 'section_cybersweep_client_overview',
        title: 'Client Information',
        columns: 2,
        fields: ['firstName', 'lastName', 'clientPersonalPhone', 'personalEmailAddress'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_cybersweep_program',
        title: 'CyberSweep Program Details',
        columns: 2,
        fields: ['cyberSweepStartDate', 'cyberSweepEndDate', 'cyberSweepStage', 'cyberSweepStatus'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_cybersweep_audit',
        title: 'Audit Information',
        columns: 2,
        fields: ['auditId', 'lastAuditDate', 'nextAuditDate', 'auditStatus'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_cybersweep_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdBy', 'createdDate', 'lastModifiedBy', 'lastModifiedDate'],
        order: 4,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // CREDIT BLOCK CLIENT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_client_creditblock',
    name: 'Credit Block Client Layout',
    objectApiName: 'Client__c',
    applicationName: 'CreditBlock',
    isDefault: false,
    description: 'Specialized layout for Credit Block client records with bureau protection details',
    sections: [
      {
        id: 'section_creditblock_basic',
        title: 'Client Information',
        columns: 2,
        fields: ['accountName', 'accountOwner', 'phone', 'email', 'dateOfBirth'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_creditblock_status',
        title: 'Credit Block Status',
        columns: 2,
        fields: ['subscriptionStatus', 'experianStatus', 'equifaxStatus', 'transunionStatus'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_creditblock_subscription',
        title: 'Subscription Details',
        columns: 2,
        fields: ['enrollmentDate', 'renewalDate', 'subscriptionTier', 'paymentStatus'],
        order: 3,
        collapsible: false
      },
      {
        id: 'section_creditblock_contact',
        title: 'Contact Information',
        columns: 2,
        fields: ['mobile', 'teamEmail', 'mailingStreet', 'mailingCity', 'mailingState', 'mailingZip'],
        order: 4,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_creditblock_management',
        title: 'Account Management',
        columns: 2,
        fields: ['managingFirm', 'accountManager', 'dayToDayContact'],
        order: 5,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_creditblock_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdDate', 'lastModified', 'createdBy', 'lastModifiedBy'],
        order: 6,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // CREDIT REPAIR CLIENT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_client_creditrepair',
    name: 'Credit Repair Client Layout',
    objectApiName: 'Client__c',
    applicationName: 'CreditRepair',
    isDefault: false,
    description: 'Specialized layout for Credit Repair client records',
    sections: [
      {
        id: 'section_creditrepair_basic',
        title: 'Client Information',
        columns: 2,
        fields: ['accountName', 'accountOwner', 'phone', 'email'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_creditrepair_scores',
        title: 'Credit Scores',
        columns: 2,
        fields: ['initialScore', 'currentScore', 'targetScore', 'scoreImprovement'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_creditrepair_status',
        title: 'Repair Status',
        columns: 2,
        fields: ['status', 'enrollmentDate', 'lastUpdate', 'creditMonitoring'],
        order: 3,
        collapsible: false
      },
      {
        id: 'section_creditrepair_contact',
        title: 'Contact Information',
        columns: 2,
        fields: ['mobile', 'teamEmail', 'mailingStreet', 'mailingCity', 'mailingState', 'mailingZip'],
        order: 4,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_creditrepair_management',
        title: 'Account Management',
        columns: 2,
        fields: ['managingFirm', 'accountManager', 'dayToDayContact'],
        order: 5,
        collapsible: true,
        defaultCollapsed: false
      }
    ]
  },

  // ============================================================
  // ID THEFT RECOVERY CLIENT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_client_idtheftrecovery',
    name: 'ID Theft Recovery Client Layout',
    objectApiName: 'Client__c',
    applicationName: 'IDTheftRecovery',
    isDefault: false,
    description: 'Specialized layout for ID Theft Recovery client records',
    sections: [
      {
        id: 'section_idtheft_basic',
        title: 'Client Information',
        columns: 2,
        fields: ['accountName', 'accountOwner', 'phone', 'email'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_idtheft_case',
        title: 'Case Information',
        columns: 2,
        fields: ['status', 'caseOpenDate', 'lastUpdate', 'fraudulentAccountsCount'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_idtheft_contact',
        title: 'Contact Information',
        columns: 2,
        fields: ['mobile', 'teamEmail', 'mailingStreet', 'mailingCity', 'mailingState', 'mailingZip'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_idtheft_management',
        title: 'Account Management',
        columns: 2,
        fields: ['managingFirm', 'accountManager', 'dayToDayContact'],
        order: 4,
        collapsible: true,
        defaultCollapsed: false
      }
    ]
  },

  // ============================================================
  // CREDIT DEVELOPMENT CLIENT PAGE LAYOUT
  // ============================================================
  {
    id: 'layout_client_creditdevelopment',
    name: 'Credit Development Client Layout',
    objectApiName: 'Client__c',
    applicationName: 'CreditDevelopment',
    isDefault: false,
    description: 'Specialized layout for Credit Development client records',
    sections: [
      {
        id: 'section_creditdev_basic',
        title: 'Client Information',
        columns: 2,
        fields: ['accountName', 'accountOwner', 'phone', 'email'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_creditdev_scores',
        title: 'Credit Development Progress',
        columns: 2,
        fields: ['currentScore', 'targetScore', 'creditHistory', 'authorizedUserAccounts'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_creditdev_status',
        title: 'Development Status',
        columns: 2,
        fields: ['status', 'enrollmentDate', 'creditMonitoring'],
        order: 3,
        collapsible: false
      },
      {
        id: 'section_creditdev_contact',
        title: 'Contact Information',
        columns: 2,
        fields: ['mobile', 'teamEmail', 'mailingStreet', 'mailingCity', 'mailingState', 'mailingZip'],
        order: 4,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_creditdev_management',
        title: 'Account Management',
        columns: 2,
        fields: ['managingFirm', 'accountManager', 'dayToDayContact'],
        order: 5,
        collapsible: true,
        defaultCollapsed: false
      }
    ]
  },

  // ============================================================
  // CS AUDIT PAGE LAYOUT (CyberSweep)
  // ============================================================
  {
    id: 'layout_cs_audit_default',
    name: 'CS Audit Layout',
    objectApiName: 'CSAudit__c',
    applicationName: 'CyberSweep',
    isDefault: true,
    description: 'Default layout for CyberSweep CS Audit records',
    sections: [
      {
        id: 'section_cs_audit_info',
        title: 'Audit Information',
        columns: 2,
        fields: ['name', 'clientId', 'clientName', 'auditType', 'fiscalYear', 'assignedAnalyst'],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_cs_audit_phase',
        title: 'Phase Status',
        columns: 2,
        fields: ['auditPhase', 'phaseStatus', 'status', 'dueDate'],
        order: 2,
        collapsible: false
      },
      {
        id: 'section_cs_audit_dates',
        title: 'Phase Schedule',
        columns: 2,
        fields: ['phase1DueDate', 'phase2DueDate', 'completionDate', 'websiteCount'],
        order: 3,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_cs_audit_notes',
        title: 'Roadblock Notes',
        columns: 1,
        fields: ['roadblockNotes'],
        order: 4,
        collapsible: true,
        defaultCollapsed: false
      },
      {
        id: 'section_cs_audit_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdDate', 'lastModified'],
        order: 5,
        collapsible: true,
        defaultCollapsed: true
      }
    ]
  },

  // ============================================================
  // PAW RECORD PAGE LAYOUT (CyberSweep)
  // Matches Salesforce PAW record detail layout
  // ============================================================
  {
    id: 'layout_paw_record_default',
    name: 'PAW Record Layout',
    objectApiName: 'PAWRecord__c',
    applicationName: 'CyberSweep',
    isDefault: true,
    description: 'Default layout for CyberSweep PAW (Performed Audit Website) records',
    sections: [
      {
        id: 'section_paw_details',
        title: 'PAW Details',
        columns: 2,
        fields: [
          'name',           // Performed Audit Website Name (left col)
          'auditPhase',     // Audit Phase (right col)
          'csAuditId',      // CS Audit (left col)
          'findings',       // Findings (right col)
          'auditTemplate',  // Audit Template (left col)
          'method',         // Method (right col)
          'website',        // Website (left col)
          'piiFound',       // PII Found (right col)
          'phase2Required', // Phase 2 Required (left col)
          'clientInfoLinks' // Link(s) for client information (right col)
        ],
        order: 1,
        collapsible: false
      },
      {
        id: 'section_paw_system',
        title: 'System Information',
        columns: 2,
        fields: ['createdBy', 'lastModifiedBy'],
        order: 2,
        collapsible: true,
        defaultCollapsed: false
      }
    ]
  }
];

/**
 * PERSISTED STORAGE
 *
 * Page layouts are persisted to localStorage so admins can modify them
 * without code changes.
 */
export const persistedPageLayouts = createPersistedMockData(
  'crm_page_layouts',
  defaultPageLayouts
);

/**
 * Helper function to get page layout by object name
 */
export function getPageLayout(objectApiName: string, layoutName?: string): PageLayout | null {
  const layouts = persistedPageLayouts.get();

  // Find layouts for this object
  const objectLayouts = layouts.filter(
    (layout: PageLayout) => layout.objectApiName === objectApiName
  );

  if (objectLayouts.length === 0) {
    return null;
  }

  // Get specific layout or default
  if (layoutName) {
    return objectLayouts.find((l: PageLayout) => l.name === layoutName) || null;
  }

  return objectLayouts.find((l: PageLayout) => l.isDefault) || objectLayouts[0];
}

/**
 * Helper function to update a page layout
 */
export function updatePageLayout(layoutId: string, updates: Partial<PageLayout>): void {
  const layouts = persistedPageLayouts.get();
  const index = layouts.findIndex((l: PageLayout) => l.id === layoutId);

  if (index !== -1) {
    const updated = [...layouts];
    updated[index] = { ...updated[index], ...updates };
    persistedPageLayouts.set(updated);
  }
}
